"use client"

import Link from "next/link"
import { Mail, Phone, MapPin } from "lucide-react"
import { WhatsApp } from "./ui/whatsapp"
import { Instagram } from "./ui/instagram"
import { Facebook } from "./ui/facebook"
import { TikTok } from "./ui/tiktok"
import { usePathname } from "next/navigation"
import { useState, useEffect } from "react"

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const pathname = usePathname()
  const [isScrolled, setIsScrolled] = useState(false)

  const isAnimatedRoute = pathname === "/" || pathname.startsWith("/auth")

  useEffect(() => {
    if (isAnimatedRoute) {
      const handleScroll = () => {
        setIsScrolled(window.scrollY > 10)
      }

      window.addEventListener("scroll", handleScroll)
      return () => window.removeEventListener("scroll", handleScroll)
    } else {
      setIsScrolled(true)
    }
  }, [isAnimatedRoute])

  const footerBackground = isScrolled ? "bg-gray-900 text-white" : "bg-transparent text-white"

  const socialLinks = [
    { icon: <Facebook />, name: "Facebook", url: "https://www.facebook.com/profile.php?id=61554136060057" },
    { icon: <Instagram />, name: "Instagram", url: "https://www.instagram.com/alquileres.temporal?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" },
    { icon: <WhatsApp />, name: "WhatsApp", url: "https://wa.me/+5493465650191" },
    { icon: <TikTok />, name: "Tiktok", url: "" }
  ]

  return (
    <footer className={`${footerBackground} mt-auto transition-colors duration-300`}>
      <div className="container px-4 mx-auto pt-12 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info - Social Links */}
          <div>
            <h2 className="font-playfair text-xl font-bold mb-4">Alquileres Temporales</h2>
            <p className="text-gray-400 mb-4">
              Ofrecemos apartamentos de lujo para alquiler temporal en Rosario y Firmat, Santa Fe.
            </p>

            <div className="flex gap-3 mb-6">
              {socialLinks.map((social, index) => (
                <Link
                  key={index}
                  href={social.url}
                  className="text-gray-400 hover:text-white transition-colors bg-gray-800 hover:bg-gray-700 p-2 rounded-full"
                  aria-label={social.name}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {social.icon}
                  <span className="sr-only">{social.name}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-4">Enlaces Rápidos</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/" className="text-gray-400 hover:text-white transition-colors">
                  Inicio
                </Link>
              </li>
              <li>
                <Link href="/apartments" className="text-gray-400 hover:text-white transition-colors">
                  Apartamentos
                </Link>
              </li>
              <li>
                <Link href="/about" className="text-gray-400 hover:text-white transition-colors">
                  Nosotros
                </Link>
              </li>
              <li>
                <Link href="/contact" className="text-gray-400 hover:text-white transition-colors">
                  Contacto
                </Link>
              </li>
              <li>
                <Link href="/faq" className="text-gray-400 hover:text-white transition-colors">
                  Preguntas Frecuentes
                </Link>
              </li>
            </ul>
          </div>

          {/* Policies */}
          <div>
            <h3 className="font-bold text-lg mb-4">Políticas</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/terms" className="text-gray-400 hover:text-white transition-colors">
                  Términos y Condiciones
                </Link>
              </li>
              <li>
                <Link href="/privacy" className="text-gray-400 hover:text-white transition-colors">
                  Política de Privacidad
                </Link>
              </li>
              <li>
                <Link href="/cancellation" className="text-gray-400 hover:text-white transition-colors">
                  Política de Cancelación
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-4">Contacto</h3>
            <address className="not-italic space-y-4">
              <div className="flex gap-3">
                <MapPin className="h-5 w-5 text-primary-gold mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">Carla Maldonado</p>
                  <a
                    href="https://www.google.com/maps/search/Rosario,+Santa+Fe,+Argentina"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Rosario, Santa Fe, Argentina
                  </a>
                </div>
              </div>
              <div className="flex gap-3">
                <Phone className="h-5 w-5 text-primary-gold mt-1 flex-shrink-0" />
                <div className="flex flex-col">
                  <a href="tel:+5493465666803" className="text-gray-400 hover:text-white transition-colors">
                    +54 9 3465 666803
                  </a>
                  <a href="tel:+5493465650191" className="text-gray-400 hover:text-white transition-colors">
                    +54 9 3465 650191
                  </a>
                </div>
              </div>
              <div className="flex gap-3">
                <Mail className="h-5 w-5 text-primary-gold mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:carla_jmaldonado@outlook.com"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  carla_jmaldonado@outlook.com
                </a>
              </div>
            </address>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 mt-8 pt-6 text-center text-gray-500 text-sm">
          <p>
            &copy; {currentYear} Alquileres Temporarios. Todos los derechos reservados. | developed by{" "}
            <Link
              href="https://github.com/codingdiego"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors"
            >
              CodingDiego
            </Link>
          </p>
        </div>
      </div>
    </footer>
  )
}